/*
 * Copyright Higharc Inc. 2019
 */
import { productLog } from "common/utils/logging";
import { Box3, Layers, Matrix4, Mesh, Object3D, Vector3 } from "three";
import type { UpdateProbeParams } from "./SHLightProbe";
import { SHLightProbe } from "./SHLightProbe";
import { LightProbeVisualizationMode } from "./SHLightProbeConstants";
import { SHLightProbeGridData, type SHLightProbeGridForData } from "./SHLightProbeGridData";

// Returns a map from Object3D id to its bounding box.
function computeMeshBoundingBoxes(scene) {
  const startTime = performance.now();

  const boundingBoxes = {};

  scene.traverseVisible(function (obj) {
    if (obj instanceof Mesh) {
      const objBox = new Box3().setFromObject(obj);
      boundingBoxes[obj.id] = objBox;
    }
  });

  productLog(`Computed mesh bounding boxes: ${performance.now() - startTime} ms`);

  return boundingBoxes;
}

// Returns true in case any box in boundingBoxes intersects the given box.
function doBoxesIntersect(boundingBoxes, queryBox) {
  let intersected = false;

  const boxes = Object.values(boundingBoxes as Record<string, Box3>);
  for (let i = 0, len = boxes.length; i < len; ++i) {
    if (boxes[i].intersectsBox(queryBox)) {
      intersected = true;
      break;
    }
  }

  return intersected;
}

// This represents a regular grid of light probes. It can be packed into a 2D texture so we can pass a large amount of
// light probe data into a shader.
// By default the probes in the grid are spaced one unit apart. Set the scale of the grid object to control the scaling.
//
// probeCount*: determines the amount of light probes in each dimension.
// shOrder: Spherical harmonics order that's used in case the probes are stored in SH format. Can be undefined in case sh is not used.
// Order 1 has 4 coefficients, order 2 has 9 coefficients.
// packedCubeMapResolution: Resolution of the cube maps packed to the cube map atlas.
export class SHLightProbeGrid extends Object3D implements SHLightProbeGridForData {
  renderLayers: Layers;

  worldToGridMatrix: Matrix4;

  private visualizationModeInternal: LightProbeVisualizationMode = LightProbeVisualizationMode.None;

  probesNeedUpdate: boolean;

  alwaysRenderUsingLatestData: boolean;

  metadata: any;

  lightProbes: SHLightProbe[];

  private useFloatRenderTarget: boolean;
  private shOrderInternal: number | undefined;
  probeCountX: number;
  probeCountY: number;
  probeCountZ: number;
  private worldScale: Vector3;
  packedCubeMapResolution: number;

  private packedData: { data: SHLightProbeGridData; isApproximate: boolean }[];

  asyncUpdater?: Generator<number, number, unknown>;

  constructor(
    probeCountX: number,
    probeCountY: number,
    probeCountZ: number,
    useFloatRenderTarget: boolean,
    shOrder: number | undefined,
    cubeMapRenderResolution: number,
    packedCubeMapResolution: number,
    name: string,
    renderLayers?: Layers,
    public useForGlobalIrradiance: boolean = true, // If true, the grid will apply lighting to the scene.
    generatePmrem: boolean = false,
    simulatedPrismHalfSize: Vector3 = new Vector3(100.0, 100.0, 100.0),
  ) {
    super();
    this.name = name;

    this.visible = false;

    this.useFloatRenderTarget = useFloatRenderTarget;
    this.shOrderInternal = shOrder;
    this.probeCountX = probeCountX;
    this.probeCountY = probeCountY;
    this.probeCountZ = probeCountZ;
    this.worldScale = new Vector3();
    this.worldToGridMatrix = new Matrix4();
    this.packedCubeMapResolution = packedCubeMapResolution;

    if (renderLayers) {
      this.renderLayers = renderLayers;
    } else {
      this.renderLayers = new Layers();
      this.renderLayers.enableAll();
    }

    this.lightProbes = [];

    for (let x = 0; x < this.probeCountX; ++x) {
      for (let y = 0; y < this.probeCountY; ++y) {
        for (let z = 0; z < this.probeCountZ; ++z) {
          const lightProbe = new SHLightProbe(
            this.useFloatRenderTarget,
            this.shOrderInternal,
            cubeMapRenderResolution,
            this.packedCubeMapResolution,
            generatePmrem,
            simulatedPrismHalfSize,
          );
          lightProbe.name = probeCountX > 1 || probeCountY > 1 || probeCountZ > 1 ? `${name}-${x}-${y}-${z}` : name;
          lightProbe.position.set(x, y, z);
          this.add(lightProbe);
          this.lightProbes.push(lightProbe);
        }
      }
    }

    // We store the two most recent light bounces in the array. That way other light probe grids may use the previous bounce to
    // render if needed.
    this.packedData = [];

    // Will always return the latest data from getDataForRendering(), even if the bounce count doesn't match.
    // This way the first bounce of one probe grid can be used as source data of another probe grid.
    this.alwaysRenderUsingLatestData = false;

    this.probesNeedUpdate = false;

    this.metadata = {};
    this.shOrder = this.shOrderInternal;
  }

  get visualizationMode(): LightProbeVisualizationMode {
    return this.visualizationModeInternal;
  }
  set visualizationMode(newValue: LightProbeVisualizationMode) {
    if (this.visualizationModeInternal === newValue) {
      return;
    }
    this.visualizationModeInternal = newValue;
    for (let i = 0; i < this.lightProbes.length; ++i) {
      this.lightProbes[i].visualizer.mode = newValue;
    }
  }

  // Note that changing shOrder will not automatically re-render the probes, but rather resets them to the initial
  // values.
  get shOrder(): number | undefined {
    return this.shOrderInternal;
  }
  set shOrder(newValue: number | undefined) {
    if (this.shOrderInternal === newValue) {
      return;
    }
    this.shOrderInternal = newValue;
    for (let i = 0; i < this.lightProbes.length; ++i) {
      this.lightProbes[i].shOrder = newValue;
    }
    this.reinitLightProbeData();
  }

  reinitLightProbeData() {
    this.interruptAsyncUpdate();
    this.disposePackedData();
  }

  getRenderedProbeCount() {
    let count = 0;
    for (let probeIndex = 0, len = this.lightProbes.length; probeIndex < len; ++probeIndex) {
      const probe = this.lightProbes[probeIndex];
      if (!probe.skipRender) {
        ++count;
      }
    }
    return count;
  }

  lightProbeIndex(gridX, gridY, gridZ) {
    return gridX * this.probeCountY * this.probeCountZ + gridY * this.probeCountZ + gridZ;
  }

  lightProbeGridCoordinates(probeIndex) {
    const remainder = probeIndex % (this.probeCountY * this.probeCountZ);
    const x = (probeIndex - remainder) / (this.probeCountY * this.probeCountZ);
    const z = remainder % this.probeCountZ;
    const y = (remainder - z) / this.probeCountZ;
    return [x, y, z];
  }

  gridCellDiagonal() {
    this.getWorldScale(this.worldScale);
    return Math.sqrt(
      Math.pow(this.worldScale.x, 2.0) + Math.pow(this.worldScale.y, 2.0) + Math.pow(this.worldScale.z, 2.0),
    );
  }

  gridCellWidth() {
    this.getWorldScale(this.worldScale);
    return this.worldScale.x;
  }

  private updatePackedDataFromProbes(bounceCount: number, isApproximate: boolean) {
    let i = 0;
    // We can try to recycle any but the previous packed data that we have. The previous data may be necessary for
    // rendering the same bounce to another light probe grid.
    // TODO: It could be simpler to just keep one entry in the data array for each bounce count.
    while (i < this.packedData.length - 1) {
      const recycled = this.packedData[i];
      if (recycled.data.ownedBy(this)) {
        if (recycled.data.refCount === 1 && recycled.data !== this.packedData[this.packedData.length - 1].data) {
          // Recycle the least recently updated packed data that's only used by this grid and put it to the end of the array.
          this.packedData.splice(i, 1);
          this.packedData.push({ data: recycled.data, isApproximate: isApproximate });
          recycled.data.updateFromProbes(bounceCount);
          this.onAfterUpdateFromProbes(bounceCount, isApproximate);
          return;
        }
        ++i;
      } else {
        // We'll now have our own data so we can dereference any data shared with another grid.
        --recycled.data.refCount;
        this.packedData.splice(i, 1);
      }
    }
    const newData = new SHLightProbeGridData();
    newData.initFromProbes(this);
    newData.updateFromProbes(bounceCount);
    this.packedData.push({ data: newData, isApproximate: isApproximate });
    this.onAfterUpdateFromProbes(bounceCount, isApproximate);
  }

  onAfterUpdateFromProbes(_bounceCount, _isApproximate) {}

  useDataAsApproximation(data) {
    if (!data.ownedBy(this)) {
      ++data.refCount;
    }
    this.packedData.push({ data: data, isApproximate: true });
  }

  setProbeSkipRender(gridX, gridY, gridZ, skipRender) {
    const probeIndex = this.lightProbeIndex(gridX, gridY, gridZ);
    this.lightProbes[probeIndex].skipRender = skipRender;
  }

  setSkipRenderOnAllProbes(skipRender) {
    for (let probeIndex = 0; probeIndex < this.lightProbes.length; ++probeIndex) {
      this.lightProbes[probeIndex].skipRender = skipRender;
    }
  }

  skipRenderFarFromGeometry(scene, boundingBoxScale) {
    const sceneBoxes = computeMeshBoundingBoxes(scene);

    // TODO: The probes at the edges of the grid should not be marked unused.
    // TODO: The bounding box of the probes should be rotated.
    const min = new Vector3();
    const max = new Vector3();

    for (let probeIndex = 0; probeIndex < this.lightProbes.length; ++probeIndex) {
      const probe = this.lightProbes[probeIndex];
      probe.getWorldPosition(min);
      probe.getWorldPosition(max);
      min.addScalar(-this.gridCellWidth() * boundingBoxScale);
      max.addScalar(this.gridCellWidth() * boundingBoxScale);
      const probeBox = new Box3(min, max);
      if (!doBoxesIntersect(sceneBoxes, probeBox)) {
        probe.skipRender = true;
      }
    }
  }

  private interpolateProbe(gridX, gridY, gridZ) {
    // Set the probe data based on neighbors that don't have the skipRender flag.
    // Neighboring probes are selected from probes on the same row or column as the probe, with only the nearest
    // probes being taken into account.
    let nearestDistance = Infinity;
    const nearestProbes: any[] = [];

    const probeCoords = [gridX, gridY, gridZ];
    const probeCounts = [this.probeCountX, this.probeCountY, this.probeCountZ];

    for (let dimension = 0; dimension < 3; ++dimension) {
      const neighborGridCoords = [gridX, gridY, gridZ];
      while (neighborGridCoords[dimension] > 0) {
        --neighborGridCoords[dimension];
        const candidateDistance = probeCoords[dimension] - neighborGridCoords[dimension];
        if (candidateDistance > nearestDistance) {
          break;
        }
        const neighborIndex = this.lightProbeIndex(neighborGridCoords[0], neighborGridCoords[1], neighborGridCoords[2]);
        const neighbor = this.lightProbes[neighborIndex];
        if (!neighbor.skipRender) {
          if (candidateDistance < nearestDistance) {
            nearestDistance = candidateDistance;
            nearestProbes.splice(0, nearestProbes.length);
          }
          nearestProbes.push(neighbor);
        }
      }

      neighborGridCoords[dimension] = probeCoords[dimension];
      while (neighborGridCoords[dimension] + 1 < probeCounts[dimension]) {
        ++neighborGridCoords[dimension];
        const candidateDistance = neighborGridCoords[dimension] - probeCoords[dimension];
        if (candidateDistance > nearestDistance) {
          break;
        }
        const neighborIndex = this.lightProbeIndex(neighborGridCoords[0], neighborGridCoords[1], neighborGridCoords[2]);
        const neighbor = this.lightProbes[neighborIndex];
        if (!neighbor.skipRender) {
          if (candidateDistance < nearestDistance) {
            nearestDistance = candidateDistance;
            nearestProbes.splice(0, nearestProbes.length);
          }
          nearestProbes.push(neighbor);
        }
      }
    }

    const probe = this.lightProbes[this.lightProbeIndex(gridX, gridY, gridZ)];
    probe.interpolateFrom(nearestProbes);
  }

  private interpolateSkippedProbes() {
    for (let gridY = 0; gridY < this.probeCountY; ++gridY) {
      for (let gridZ = 0; gridZ < this.probeCountZ; ++gridZ) {
        for (let gridX = 0; gridX < this.probeCountX; ++gridX) {
          const probe = this.lightProbes[this.lightProbeIndex(gridX, gridY, gridZ)];
          if (probe.skipRender) {
            this.interpolateProbe(gridX, gridY, gridZ);
          }
        }
      }
    }
  }

  asyncUpdateFromScene(updateParams: Omit<UpdateProbeParams, "renderLayers">) {
    this.interruptAsyncUpdate();
    this.asyncUpdater = SHLightProbeGrid.shLightProbeGridAsyncUpdate(this, {
      ...updateParams,
      renderLayers: this.renderLayers,
    });
    return this.asyncUpdater;
  }

  private interruptAsyncUpdate() {
    if (this.asyncUpdater !== undefined) {
      this.asyncUpdater.return(0);
      this.asyncUpdater = undefined;
    }
  }

  updatePackedDataFromApproximateProbes(bounceCount) {
    this.interpolateSkippedProbes();
    this.updatePackedDataFromProbes(bounceCount, true);
  }

  updateFromScene(updateParams: Omit<UpdateProbeParams, "renderLayers">) {
    this.interruptAsyncUpdate();

    const startTime = performance.now();

    const asyncUpdater = SHLightProbeGrid.shLightProbeGridAsyncUpdate(this, {
      ...updateParams,
      renderLayers: this.renderLayers,
    });
    let iterator = asyncUpdater.next();
    while (!iterator.done) {
      iterator = asyncUpdater.next();
    }
    const updatedProbeCount = iterator.value;

    productLog(
      `Updated light probe grid with ${updatedProbeCount} probes from scene: ${performance.now() - startTime} ms`,
    );
  }

  getData(bounceCount, isApproximate) {
    for (let i = this.packedData.length - 1; i >= 0; --i) {
      if (
        this.packedData[i].data.bounceCount === bounceCount &&
        (isApproximate === undefined || this.packedData[i].isApproximate === isApproximate)
      ) {
        return this.packedData[i].data;
      }
    }
    return null;
  }

  getDataForRendering(bounceCount, isApproximate) {
    for (let i = this.packedData.length - 1; i >= 0; --i) {
      if (
        this.alwaysRenderUsingLatestData ||
        (this.packedData[i].data.bounceCount === bounceCount &&
          (isApproximate === undefined || this.packedData[i].isApproximate === isApproximate))
      ) {
        return this.packedData[i].data;
      }
    }
    return null;
  }

  private disposePackedData() {
    for (let i = 0; i < this.packedData.length; ++i) {
      if (this.packedData[i].data.ownedBy(this)) {
        this.packedData[i].data.dispose();
      } else {
        --this.packedData[i].data.refCount;
      }
    }
    this.packedData = [];
  }

  dispose() {
    this.disposePackedData();

    for (let probeIndex = 0; probeIndex < this.lightProbes.length; ++probeIndex) {
      this.lightProbes[probeIndex].dispose();
    }
  }

  static *shLightProbeGridAsyncUpdate(probeGrid: SHLightProbeGrid, updateParams: UpdateProbeParams) {
    probeGrid.updateMatrixWorld();
    probeGrid.worldToGridMatrix.copy(probeGrid.matrixWorld).invert();

    let updatedProbeCount = 0;

    for (let probeIndex = 0; probeIndex < probeGrid.lightProbes.length; ++probeIndex) {
      const probe = probeGrid.lightProbes[probeIndex];
      if (!probe.skipRender) {
        const iterator = probe.updateFromScene(updateParams);
        while (!iterator.next().done) {
          yield updatedProbeCount;
        }
        ++updatedProbeCount;
      }
    }

    probeGrid.interpolateSkippedProbes();
    yield updatedProbeCount;

    probeGrid.updatePackedDataFromProbes(updateParams.bounceIndex + 1, false);

    return updatedProbeCount;
  }
}
